/**
 * BudgetDashboardService
 * Org-wide budget dashboard: consumption overview, unbudgeted spend queue,
 * the manager-scoped my-overview and grouped analytics (+ matrix).
 * Route: api/budget-dashboard
 */
export class BudgetDashboardService {
    constructor(http, config) {
        this.http = http;
        const servicePath = config?.servicePath ?? BudgetDashboardService.DEFAULT_SERVICE_PATH;
        this.basePath = `${servicePath}/${BudgetDashboardService.BASE_PATH}`;
    }
    buildUrl(path) {
        const url = path ? `${this.basePath}/${path}` : this.basePath;
        return url.startsWith('/') ? url : '/' + url;
    }
    /** Totals, by-type consumption, top consumers, at-risk budgets and recent alerts. */
    async getOverview() {
        const response = await this.http.get(this.buildUrl('overview'));
        if (!response.data)
            throw new Error('Budget dashboard overview not available');
        return response.data;
    }
    /** One page of the unbudgeted spend queue. */
    async getUnbudgeted(page = 1, pageSize = 50) {
        const response = await this.http.get(this.buildUrl(`unbudgeted?page=${page}&pageSize=${pageSize}`));
        if (!response.data)
            throw new Error('Unbudgeted transactions not available');
        return response.data;
    }
    /** The BudgetManager-scoped dashboard: my budgets, alerts and open change requests. */
    async getMyOverview() {
        const response = await this.getMyOverviewRaw();
        if (!response.data)
            throw new Error('Budget my-overview not available');
        return response.data;
    }
    getMyOverviewRaw() {
        return this.http.get(this.buildUrl('my-overview'));
    }
    /**
     * Grouped analytics: groupBy=department|location|project|user|supplier.
     *
     * from/to (yyyyMMddHHmmss) slice SPEND only — allocated and released-to-date are budget-side
     * figures, not time series, and are always whole-of-budget.
     */
    async getAnalytics(query) {
        const response = await this.getAnalyticsRaw(query);
        return response.data ?? [];
    }
    getAnalyticsRaw(query) {
        const params = new URLSearchParams();
        if (query?.groupBy !== undefined)
            params.append('groupBy', query.groupBy);
        if (query?.from !== undefined)
            params.append('from', String(query.from));
        if (query?.to !== undefined)
            params.append('to', String(query.to));
        if (query?.typeId !== undefined)
            params.append('typeId', String(query.typeId));
        const qs = params.toString();
        const url = this.buildUrl('analytics');
        return this.http.get(qs ? `${url}?${qs}` : url);
    }
    /** Cross-dimension spend matrix: rows/cols ∈ department|location|project (must differ). */
    async getMatrix(rows = 'department', cols = 'location') {
        const response = await this.getMatrixRaw(rows, cols);
        if (!response.data)
            throw new Error('Budget analytics matrix not available');
        return response.data;
    }
    getMatrixRaw(rows = 'department', cols = 'location') {
        const params = new URLSearchParams({ rows, cols });
        return this.http.get(`${this.buildUrl('analytics/matrix')}?${params.toString()}`);
    }
}
BudgetDashboardService.DEFAULT_SERVICE_PATH = 'budgets';
BudgetDashboardService.BASE_PATH = 'api/budget-dashboard';
//# sourceMappingURL=BudgetDashboardService.js.map